import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { PracticeTimerProvider } from "@/contexts/PracticeTimerContext";
import { MetronomeSettingsProvider } from "@/contexts/MetronomeSettingsContext";
import { PremiumProvider } from "@/contexts/PremiumContext";
import { MidiProvider } from "@/contexts/MidiContext";
import { AuthProvider, useAuth } from "@/components/auth/AuthProvider";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import AdminRoute from "@/components/auth/AdminRoute";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import AppFooter from "@/components/AppFooter";
import { CookieConsentBanner, CookieSettingsButton } from "@/components/CookieConsentBanner";
import FloatingFeedbackButton from "@/components/feedback/FloatingFeedbackButton";
import { Loader2 } from "lucide-react";
import IncompleteProfileBanner from "@/components/IncompleteProfileBanner";
import DocumentTitleManager from "@/components/DocumentTitleManager";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

const Index = lazy(() => import("./pages/Index"));
const Stats = lazy(() => import("./pages/Stats"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const Admin = lazy(() => import("./pages/Admin"));
const SessionDetail = lazy(() => import("./pages/SessionDetail"));
const SessionModeV2 = lazy(() => import("./pages/SessionModeV2"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const WaitlistConfirm = lazy(() => import("./pages/WaitlistConfirm"));
const WaitlistTrack = lazy(() => import("./pages/WaitlistTrack"));
const ResendInvite = lazy(() => import("./pages/ResendInvite"));
const AuthVerify = lazy(() => import("./pages/AuthVerify"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Imprint = lazy(() => import("./pages/Imprint"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Faq = lazy(() => import("./pages/Faq"));
const Feedback = lazy(() => import("./pages/Feedback"));
const FeedbackDetailPage = lazy(() => import("./components/feedback/FeedbackDetail"));
const DrumPatternLab = lazy(() => import("./pages/DrumPatternLab"));

const queryClient = new QueryClient();

function AppRoutes() {
  const { session, role, profile, loading: authLoading } = useAuth();
  const { active, message, loading: maintenanceLoading } = useMaintenanceMode();

  if (maintenanceLoading) return <div className="flex items-center justify-center min-h-[50vh]"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  const isAdmin = role === "admin";
  const hasBypass = profile?.maintenance_bypass === true;

  // Maintenance mode: only admins and bypass users get through
  if (active && !isAdmin && !hasBypass) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/verify" element={<AuthVerify />} />
        <Route path="/imprint" element={<Imprint />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/faq/*" element={<Faq />} />
        <Route path="/en/faq/*" element={<Faq />} />
        <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
        <Route path="/waitlist/confirm" element={<WaitlistConfirm />} />
        <Route path="/waitlist/track" element={<WaitlistTrack />} />
        <Route path="/waitlist/resend-invite" element={<ResendInvite />} />
        <Route path="*" element={<Maintenance message={message} />} />
      </Routes>
    );
  }

  // Not logged in: show landing page with waitlist, login, and legal pages
  if (!active && !session && !authLoading) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/verify" element={<AuthVerify />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/imprint" element={<Imprint />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/faq/*" element={<Faq />} />
        <Route path="/en/faq/*" element={<Faq />} />
        <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
        <Route path="/waitlist/confirm" element={<WaitlistConfirm />} />
        <Route path="/waitlist/track" element={<WaitlistTrack />} />
        <Route path="/waitlist/resend-invite" element={<ResendInvite />} />
        <Route path="*" element={<Maintenance variant="landing" />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/verify" element={<AuthVerify />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/imprint" element={<Imprint />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/faq/*" element={<Faq />} />
      <Route path="/en/faq/*" element={<Faq />} />
      <Route path="/" element={<Index />} />
      <Route path="/goals" element={<Navigate to="/" replace />} />
      <Route path="/journal" element={<Navigate to="/" replace />} />
      <Route path="/stats" element={<Stats />} />
      <Route path="/drum-pattern" element={<Navigate to="/drum-lab" replace />} />
      <Route path="/upgrade" element={<Upgrade />} />
      <Route path="/feedback" element={<Feedback />} />
      <Route path="/feedback/:id" element={<FeedbackDetailPage />} />
      <Route path="/drum-lab" element={<DrumPatternLab />} />
      <Route path="/practice" element={<Navigate to="/session-v2" replace />} />
      <Route path="/session-v2" element={<ProtectedRoute><SessionModeV2 /></ProtectedRoute>} />
      <Route path="/session/:id" element={<ProtectedRoute><SessionDetail /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
      <Route path="/waitlist/confirm" element={<WaitlistConfirm />} />
      <Route path="/waitlist/track" element={<WaitlistTrack />} />
      <Route path="/waitlist/resend-invite" element={<ResendInvite />} />
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <BrowserRouter>
        <AuthProvider>
          <PracticeTimerProvider>
            <MetronomeSettingsProvider>
            <PremiumProvider>
            <MidiProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <DocumentTitleManager />
              <IncompleteProfileBanner />
              <div className="flex min-h-screen flex-col">
                <div className="flex-1">
                  <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>}>
                    <AppRoutes />
                  </Suspense>
                </div>
                <AppFooter />
              </div>
              <CookieConsentBanner />
              <CookieSettingsButton />
              <FloatingFeedbackButton />
              <Analytics />
              <SpeedInsights />
            </TooltipProvider>
            </MidiProvider>
            </PremiumProvider>
            </MetronomeSettingsProvider>
          </PracticeTimerProvider>
        </AuthProvider>
      </BrowserRouter>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
